// WORKING ACCUMULATION WRAPPER FROM FINAL FOLDER
import React, { useState, useRef, useEffect } from 'react';
import AccumulationOriginal from './AccumulationOriginal';
import { SimulationEngine } from './SimulationEngine';
import PDFExportManager from './PDFExportManager';
import CalculatorBridge from './CalculatorBridge';
import CookieConsent from './components/CookieConsent.js';
import { EmailCaptureForm } from './components/EmailCaptureForm.js';
import { SubscriptionManager } from './SubscriptionManager.js';
import { openPricingModal } from './utils/openPricingModal.js';
import { alertCheckoutFailure, startRecommendedCheckout } from './utils/recommendedCheckout.js';
import { buildAccumulationOneTimeContributions } from './accumulationWhatIfEngine.js';

const WorkingAccumulationWrapper = ({ navigateToCalculator }) => {
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);
  
  // Default inputs (used for initial state and for reverting free users after run)
  const DEFAULT_ACCUMULATION_INPUTS = {
    accumulationRelationshipStatus: 'single',
    currentAge: 45,
    retirementAge: 67,
    currentSuper: 100000,
    currentWage: 0,
    wageGrowthRate: 0.3,
    additionalContributions: 0,
    nonConcessionalContributions: 0,
    enableBringForward: false,
    unusedConcessionalContributions: 0,
    downsizerContribution: 0,
    downsizerYear: 0,
    spouseContribution: 0,
    enableCoContribution: true,
    enableLISTO: true,
    listoAdjustedTaxableIncome: '',
    partnerListoAdjustedTaxableIncome: '',
    enableTTR: false,
    ttrStartAge: 60,
    ttrDrawdownRate: 4,
    accumulationPhasePercent: 100,
    partnerEnableTTR: false,
    partnerTtrStartAge: 60,
    partnerTtrDrawdownRate: 4,
    partnerAccumulationPhasePercent: 100,
    enableWorkTest: true,
    partnerEnableWorkTest: true,
    portfolioTurnover: 0,
    dividendPortion: 40,
    partnerCurrentAge: 0,
    partnerRetirementAge: 67,
    partnerCurrentSuper: 0,
    partnerCurrentWage: 0,
    partnerAdditionalContributions: 0,
    partnerNonConcessionalContributions: 0,
    partnerEnableBringForward: false,
    partnerUnusedConcessionalContributions: 0,
    partnerDownsizerContribution: 0,
    partnerDownsizerYear: 0,
    partnerSpouseContribution: 0,
    accumulationMode: 'assumed',
    assumedReturn: 7.5,
    assumedInflation: 2.5,
    adminFees: 52,
    adminFeePercentage: 0.10,
    investmentFees: 0.5,
    insuranceFees: 0,
    sp500: 35,
    ausShares: 15,
    intlShares: 20,
    bonds: 20,
    cash: 10,
    frankingCredits: 80
  };

  // Load saved inputs from LocalStorage (Pro only: save/restore is gated)
  const loadSavedInputs = () => {
    if (!SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.SAVE_RESTORE_SCENARIO)) return null;
    try {
      const saved = localStorage.getItem('accumulation_calculator_inputs');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (err) {
      console.error('Failed to load saved inputs:', err);
    }
    return null;
  };

  const [inputs, setInputs] = useState(loadSavedInputs() || DEFAULT_ACCUMULATION_INPUTS);

  const [accumulationResults, setAccumulationResults] = useState(null);
  const [transferFromSMSF, setTransferFromSMSF] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [showInputsRevertModal, setShowInputsRevertModal] = useState(false);
  const resultsRef = useRef(null);
  const lastRunInputsRef = useRef(null);
  const lastRunContributionsRef = useRef(null);

  // Homepage micro-calculator handoff: visible fields only, no hidden assumptions.
  useEffect(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search || '');
      const hash = window.location.hash || '';
      const hashParams = hash.includes('?') ? new URLSearchParams(hash.split('?')[1]) : new URLSearchParams();
      const age = urlParams.get('age') || hashParams.get('age');
      const balance = urlParams.get('balance') || hashParams.get('balance');
      const retirementAge = urlParams.get('retirement_age') || hashParams.get('retirement_age');
      const annualContributions = urlParams.get('annual_contributions') || hashParams.get('annual_contributions');
      if (!age && !balance && !retirementAge && !annualContributions) return;

      setInputs(prev => ({
        ...prev,
        currentAge: age ? Number(age) : prev.currentAge,
        currentSuper: balance ? Number(balance) : prev.currentSuper,
        retirementAge: retirementAge ? Number(retirementAge) : prev.retirementAge,
        additionalContributions: annualContributions ? Number(annualContributions) : prev.additionalContributions
      }));
    } catch (err) {
      console.error('Failed to apply homepage accumulation prefill:', err);
    }
  }, []);

  // Autosave inputs to LocalStorage whenever they change (Pro only: save/restore is gated)
  useEffect(() => {
    if (!SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.SAVE_RESTORE_SCENARIO)) return;
    try {
      localStorage.setItem('accumulation_calculator_inputs', JSON.stringify(inputs));
      setLastSaved(new Date());
    } catch (err) {
      console.error('Failed to save inputs:', err);
    }
  }, [inputs]);

  // Load transfer data from SMSF → Accumulation, if present (one-time on mount)
  useEffect(() => {
    try {
      const transfer = CalculatorBridge.retrieveTransferData('accumulation');
      if (transfer) {
        setInputs(prev => ({
          ...prev,
          ...transfer,
          accumulationMode: 'historical'
        }));
        setTransferFromSMSF(true);
        // Clear after applying
        CalculatorBridge.clearTransferData();
      }
    } catch (err) {
      console.error('Failed to apply transfer data for accumulation:', err);
    }
  }, []);

  // Update "seconds ago" display
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      forceUpdate(n => n + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Close export menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clear saved data function
  const clearSavedData = () => {
    if (window.confirm('Clear all saved data and reset to defaults? This cannot be undone.')) {
      localStorage.removeItem('accumulation_calculator_inputs');
      window.location.reload();
    }
  };

  const handleNumberChange = (field, value) => {
    // Allow empty string (user is clearing the field)
    if (value === '' || value === null || value === undefined) {
      setInputs(prev => ({ ...prev, [field]: 0 }));
      setAccumulationResults(null);
      return;
    }
    
    // Allow "-" by itself (user starting to type negative number) - will validate on blur
    if (value === '-') {
      setInputs(prev => ({ ...prev, [field]: value }));
      setAccumulationResults(null);
      return;
    }
    
    const numValue = parseFloat(value);
    
    // If parseFloat returns NaN (invalid input), don't update
    if (isNaN(numValue)) {
      return;
    }
    
    // Validation: Prevent negative numbers (but allow typing)
    if (numValue < 0) {
      alert('Value cannot be negative. Please enter a positive number.');
      return; // Don't update state
    }
    
    // Validation: Age limits
    if (field === 'currentAge' || field === 'partnerCurrentAge') {
      if (numValue > 120) {
        alert('Warning: Age over 120 is unrealistic. Please check your entry.');
        return;
      }
      if (numValue < 0) {
        alert('Age cannot be negative.');
        return;
      }
    }
    
    // Retirement age validation removed from onChange - now only validates on blur
    // This allows users to type multi-digit numbers without interruption
    
    // Validation: TTR Start Age must be >= preservation age (60)
    // Only validate if value is at least 2 digits (>= 10) to allow typing
    if (field === 'ttrStartAge') {
      const preservationAge = 60; // Standard preservation age
      if (numValue >= 10 && numValue < preservationAge) {
        alert(`TTR Start Age (${numValue}) must be at least ${preservationAge} (preservation age).`);
        return;
      }
    }
    
    if (field === 'partnerTtrStartAge') {
      const preservationAge = 60;
      if (numValue >= 10 && numValue < preservationAge) {
        alert(`Partner's TTR Start Age (${numValue}) must be at least ${preservationAge} (preservation age).`);
        return;
      }
    }
    
    // TTR Drawdown Rate validation removed from onChange - now only validates on blur
    // This allows users to type multi-digit numbers without interruption
    // onBlur validation is in AccumulationOriginal.js lines 564-571 and 815-822
    
    // Validation: Accumulation Phase Percent must be 0-100
    if (field === 'accumulationPhasePercent' || field === 'partnerAccumulationPhasePercent') {
      if (numValue < 0 || numValue > 100) {
        alert(`Accumulation Phase Percent must be between 0% and 100%. You entered ${numValue}%.`);
        return;
      }
    }
    
    // Validation: Return assumptions
    if (field === 'assumedReturn') {
      if (numValue < 0) {
        alert('Assumed Return cannot be negative.');
        return;
      }
      if (numValue > 15) {
        alert(`Warning: Assumed Return of ${numValue}% is unrealistically high. Typical range is 5-10%.`);
        return;
      }
      if (numValue < 2) {
        alert(`Warning: Assumed Return of ${numValue}% is very low. Are you sure?`);
      }
    }
    
    if (field === 'assumedInflation') {
      if (numValue < 0) {
        alert('Assumed Inflation cannot be negative (deflation scenarios not supported).');
        return;
      }
      if (numValue > 10) {
        alert(`Warning: Assumed Inflation of ${numValue}% is very high (e.g., 1970s inflation). Typical range is 2-3%. The value will be accepted.`);
        // Don't return - allow the value to be saved
      }
    }
    
    // If validation passes, update state
    setInputs(prev => ({ ...prev, [field]: numValue }));
    setAccumulationResults(null);
  };

  const handleInputChange = (field, value) => {
    setInputs(prev => ({ ...prev, [field]: value }));
    // Clear results when inputs change - user must click "Calculate Projection" to see new results
    setAccumulationResults(null);
  };

  const handleRetirementAgeBlur = (field) => {
    const value = inputs[field];
    const numValue = parseFloat(value);
    
    // Skip validation if empty or not a number
    if (!value || isNaN(numValue)) return;
    
    if (field === 'retirementAge') {
      const currentAge = inputs.currentAge || 0;
      if (numValue < currentAge) {
        alert(`ℹ️ RETIREMENT AGE:\n\nRetirement age (${numValue}) must be greater than or equal to current age (${currentAge}).\n\nPlease enter a valid retirement age.`);
        // Reset to current age
        setInputs(prev => ({ ...prev, retirementAge: currentAge }));
      } else if (numValue > 120) {
        alert(`⚠️ WARNING:\n\nRetirement age over 120 is unrealistic.\n\nPlease check your entry.`);
      }
    }
    
    if (field === 'partnerRetirementAge') {
      const partnerCurrentAge = inputs.partnerCurrentAge || 0;
      if (numValue < partnerCurrentAge) {
        alert(`ℹ️ PARTNER'S RETIREMENT AGE:\n\nRetirement age (${numValue}) must be greater than or equal to partner's current age (${partnerCurrentAge}).\n\nPlease enter a valid retirement age.`);
        // Reset to partner's current age
        setInputs(prev => ({ ...prev, partnerRetirementAge: partnerCurrentAge }));
      } else if (numValue > 120) {
        alert(`⚠️ WARNING:\n\nPartner's retirement age over 120 is unrealistic.\n\nPlease check your entry.`);
      }
    }
  };

  const handleCalculate = () => {
    handleCalculateWithInputs(inputs);
  };

  const handleCalculateWithInputs = (inputData) => {
    try {
      // Validate portfolio allocation totals 100% if in historical mode
      if (inputData.accumulationMode === 'historical') {
        const traditionalAlloc = Number(inputData.sp500 || 0) + Number(inputData.ausShares || 0) + Number(inputData.intlShares || 0) + Number(inputData.bonds || 0) + Number(inputData.cash || 0);
        const infrastructurePct = Number(inputData.infrastructurePct || 0);
        const totalAlloc = traditionalAlloc + infrastructurePct;
        if (Math.abs(totalAlloc - 100) > 0.5) {
          alert(`Cannot calculate: Asset allocation must total 100%. Current total: ${totalAlloc}%\n\nPlease adjust your portfolio allocation before calculating.`);
          return;
        }
      }
      
      const oneTimeContributions = buildAccumulationOneTimeContributions(inputData);
      const result = SimulationEngine.runAccumulationSimulation(inputData, oneTimeContributions);
      setAccumulationResults(result);
      if (!result.error) {
        lastRunInputsRef.current = { ...inputData };
        lastRunContributionsRef.current = JSON.parse(JSON.stringify(oneTimeContributions));
      }
      // Free users: revert inputs to defaults after run (subscribers keep theirs)
      if (!SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.SAVE_RESTORE_SCENARIO) && !result.error) {
        setInputs({ ...DEFAULT_ACCUMULATION_INPUTS });
        setShowInputsRevertModal(true);
      }
    } catch (error) {
      // Calculation error
      setAccumulationResults({ error: 'Calculation failed. Please check your inputs.' });
    }
  };

  // No automatic calculation on mount - wait for user to click "Calculate Projection"

  const preservationAge = 60;
  const partnerPreservationAge = 60;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', padding: '0 20px' }}>
        <h2 style={{color: 'green', margin: 0}}>WORKING ACCUMULATION CALCULATOR</h2>
        {SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.SAVE_RESTORE_SCENARIO) ? (
          <button
            onClick={clearSavedData}
            style={{
              backgroundColor: '#6b7280',
              color: 'white',
              fontWeight: 'bold',
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#4b5563'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#6b7280'}
            title="Clear all saved data and reset"
          >
            <span>🗑️</span>
            <span>Clear Data</span>
          </button>
        ) : (
          <span style={{ fontSize: '13px', color: '#6b7280', fontStyle: 'italic' }}>Subscribe to save and restore your inputs across sessions.</span>
        )}
      </div>
      <AccumulationOriginal
        inputs={inputs}
        onNumberChange={handleNumberChange}
        onInputChange={handleInputChange}
        onRetirementAgeBlur={handleRetirementAgeBlur}
        accumulationResults={accumulationResults}
        onCalculate={handleCalculate}
        preservationAge={preservationAge}
        partnerPreservationAge={partnerPreservationAge}
        resultsRef={resultsRef}
        transferFromSMSF={transferFromSMSF}
        lastRunInputsRef={lastRunInputsRef}
        lastRunContributionsRef={lastRunContributionsRef}
      />
      
      {/* Export and Continue Buttons (only show after calculation) */}
      {accumulationResults && !accumulationResults.error && (
        <div style={{ textAlign: 'center', marginTop: '20px', marginBottom: '40px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <button
              onClick={async () => {
                if (!SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.PDF_EXPORT)) {
                  startRecommendedCheckout({
                    tier: 'essential',
                    billing: 'monthly',
                    source: 'pdf_export',
                    calculatorTier: 'accumulation',
                    surface: 'accumulation_pdf_export',
                  }).catch(alertCheckoutFailure);
                  return;
                }
                try {
                  const chartImages = {};
                  
                  // Wait for charts to render
                  await new Promise(resolve => setTimeout(resolve, 1500));
                  
                  // Use same chart capture method as advanced calculator
                  const svgToImage = (svgElement, width, height) => {
                    return new Promise((resolve) => {
                      try {
                        const svgString = new XMLSerializer().serializeToString(svgElement);
                        const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
                        const url = URL.createObjectURL(svgBlob);
                        const img = new Image();
                        img.onload = () => {
                          const canvas = document.createElement('canvas');
                          canvas.width = width;
                          canvas.height = height;
                          const ctx = canvas.getContext('2d');
                          ctx.fillStyle = '#ffffff';
                          ctx.fillRect(0, 0, width, height);
                          ctx.drawImage(img, 0, 0);
                          const imageData = canvas.toDataURL('image/png');
                          URL.revokeObjectURL(url);
                          resolve({
                            data: imageData,
                            width: width,
                            height: height
                          });
                        };
                        img.onerror = () => {
                          URL.revokeObjectURL(url);
                          resolve(null);
                        };
                        img.src = url;
                      } catch (err) {
                        console.error('[PDF] SVG conversion error:', err);
                        resolve(null);
                      }
                    });
                  };
                  
                  const captureChart = async (selector, chartName) => {
                    const container = document.querySelector(selector);
                    if (!container) {
                      console.warn(`[PDF] ${chartName} container not found: ${selector}`);
                      return null;
                    }
                    
                    // Scroll into view
                    container.scrollIntoView({ behavior: 'instant', block: 'center' });
                    await new Promise(resolve => setTimeout(resolve, 500));
                    
                    // Hide tooltips
                    const tooltips = container.querySelectorAll('.recharts-tooltip-wrapper, [role="tooltip"]');
                    tooltips.forEach(t => t.style.display = 'none');
                    await new Promise(resolve => setTimeout(resolve, 200));
                    
                    // Try to find SVG element (Recharts uses SVG)
                    const svg = container.querySelector('svg');
                    if (svg) {
                      console.log(`[PDF] Found SVG for ${chartName}, converting...`);
                      const svgRect = svg.getBoundingClientRect();
                      const width = Math.max(svgRect.width || 800, 800);
                      const height = Math.max(svgRect.height || 300, 300);
                      
                      const result = await svgToImage(svg, width, height);
                      if (result) {
                        console.log(`[PDF] ${chartName} captured via SVG: ${result.width}x${result.height}`);
                        tooltips.forEach(t => t.style.display = '');
                        return result;
                      }
                    }
                    
                    // Fallback to html2canvas if SVG method fails
                    if (window.html2canvas) {
                      try {
                        console.log(`[PDF] Falling back to html2canvas for ${chartName}...`);
                        const canvas = await window.html2canvas(container, {
                          scale: 2,
                          backgroundColor: '#ffffff',
                          logging: true,
                          useCORS: true,
                          allowTaint: true
                        });
                        const imageData = canvas.toDataURL('image/png');
                        console.log(`[PDF] ${chartName} captured via html2canvas: ${canvas.width}x${canvas.height}`);
                        tooltips.forEach(t => t.style.display = '');
                        return {
                          data: imageData,
                          width: canvas.width,
                          height: canvas.height
                        };
                      } catch (err) {
                        console.error(`[PDF] html2canvas error for ${chartName}:`, err);
                        tooltips.forEach(t => t.style.display = '');
                        return null;
                      }
                    }
                    
                    tooltips.forEach(t => t.style.display = '');
                    return null;
                  };
                  
                  // Capture chart
                  console.log('[PDF] Starting chart capture for accumulation calculator...');
                  chartImages.balanceChart = await captureChart('.accumulation-chart', 'Accumulation Chart');
                  
                  const capturedCount = Object.keys(chartImages).filter(k => chartImages[k] !== null).length;
                  console.log(`[PDF] Chart capture complete: ${capturedCount} of 1 chart captured`);
                  if (capturedCount === 0) {
                    console.error('[PDF] WARNING: NO CHARTS CAPTURED! Check console for errors.');
                  }
                  
                  PDFExportManager.exportAccumulationCalculator(inputs, accumulationResults, chartImages);
                } catch (error) {
                  console.error('Error generating PDF:', error);
                  alert('Error generating PDF: ' + error.message);
                }
              }}
              style={{
                backgroundColor: '#16a34a',
                color: 'white',
                fontWeight: 'bold',
                padding: '12px 32px',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#15803d'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#16a34a'}
            >
              <svg style={{ width: '20px', height: '20px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download PDF
            </button>

            <div ref={exportMenuRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                style={{
                  backgroundColor: '#4b5563',
                  color: 'white',
                  fontWeight: 'bold',
                  padding: '12px 32px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '16px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#374151'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#4b5563'}
              >
                <svg style={{ width: '20px', height: '20px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export
                <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {showExportMenu && (
                <div style={{
                  position: 'absolute',
                  right: 0,
                  marginTop: '8px',
                  width: '192px',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                  border: '1px solid #e5e7eb',
                  zIndex: 50
                }}>
                  <button
                    onClick={async () => {
                      setShowExportMenu(false);
                      if (!SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.PDF_EXPORT)) {
                        startRecommendedCheckout({
                          tier: 'essential',
                          billing: 'monthly',
                          source: 'pdf_export',
                          calculatorTier: 'accumulation',
                          surface: 'accumulation_pdf_export_menu',
                        }).catch(alertCheckoutFailure);
                        return;
                      }
                      try {
                        const chartImages = {};
                        
                        if (typeof window.html2canvas !== 'undefined') {
                          const tooltips = document.querySelectorAll('.recharts-tooltip-wrapper');
                          tooltips.forEach(t => t.style.display = 'none');
                          
                          const balanceChart = document.querySelector('.accumulation-chart');
                          if (balanceChart) {
                            const canvas = await window.html2canvas(balanceChart, { 
                              scale: 2,
                              backgroundColor: '#ffffff',
                              logging: false
                            });
                            // Pass as object with data, width, height for proper PDF rendering
                            chartImages.balanceChart = {
                              data: canvas.toDataURL('image/png'),
                              width: canvas.width,
                              height: canvas.height
                            };
                          }
                          
                          tooltips.forEach(t => t.style.display = '');
                        }
                        
                        PDFExportManager.exportAccumulationCalculator(inputs, accumulationResults, chartImages);
                      } catch (error) {
                        console.error('Error generating PDF:', error);
                        alert('Error generating PDF: ' + error.message);
                      }
                    }}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '12px 16px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      borderRadius: '8px 8px 0 0',
                      transition: 'background-color 0.15s'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#f9fafb'}
                    onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    <svg style={{ width: '20px', height: '20px', color: '#dc2626' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    Export PDF
                  </button>
                  <button
                    onClick={() => {
                      setShowExportMenu(false);
                      if (!SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.PDF_EXPORT)) {
                        startRecommendedCheckout({
                          tier: 'essential',
                          billing: 'monthly',
                          source: 'pdf_export',
                          calculatorTier: 'accumulation',
                          surface: 'accumulation_csv_export_menu',
                        }).catch(alertCheckoutFailure);
                        return;
                      }
                      PDFExportManager.exportAccumulationToCSV(inputs, accumulationResults);
                    }}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '12px 16px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      borderTop: '1px solid #f3f4f6',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      borderRadius: '0 0 8px 8px',
                      transition: 'background-color 0.15s'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#f9fafb'}
                    onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    <svg style={{ width: '20px', height: '20px', color: '#16a34a' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export CSV
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={() => {
                const transferData = CalculatorBridge.accumulationToRetirement(inputs, accumulationResults, 'basic');
                CalculatorBridge.storeTransferData(transferData, 'accumulation', 'basic');
                CalculatorBridge.saveTransferData('basic', transferData);
                // Navigate to Basic Retirement Calculator
                if (navigateToCalculator) {
                  navigateToCalculator('basic');
                } else {
                  window.location.href = '/?calculator=basic';
                }
              }}
              style={{
                backgroundColor: '#2563eb',
                color: 'white',
                fontWeight: 'bold',
                padding: '12px 32px',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#1d4ed8'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#2563eb'}
            >
              <svg style={{ width: '20px', height: '20px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
              Continue to Basic Retirement
            </button>

            <button
              onClick={() => {
                const transferData = CalculatorBridge.accumulationToRetirement(inputs, accumulationResults, 'advanced');
                CalculatorBridge.storeTransferData(transferData, 'accumulation', 'advanced');
                CalculatorBridge.saveTransferData('advanced', transferData);
                // Navigate to Advanced Retirement Calculator
                if (navigateToCalculator) {
                  navigateToCalculator('advanced');
                } else {
                  window.location.href = '/?calculator=advanced';
                }
              }}
              style={{
                backgroundColor: '#7c3aed',
                color: 'white',
                fontWeight: 'bold',
                padding: '12px 32px',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#6d28d9'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#7c3aed'}
            >
              <svg style={{ width: '20px', height: '20px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
              Continue to Advanced
            </button>
          </div>
          <EmailCaptureForm
            calculatorType="accumulation"
            results={{
              totalFinalBalance: accumulationResults?.finalBalances?.total ?? accumulationResults?.finalBalance ?? 0,
              avgAnnualContributions: accumulationResults?.totalContributions
                ? Math.round(accumulationResults.totalContributions / Math.max(1, (inputs.retirementAge || 67) - (inputs.currentAge || 30)))
                : 0,
              retirementAge: inputs.retirementAge ?? 67
            }}
            inputs={inputs}
            getPdfBase64={async () => PDFExportManager.exportAccumulationCalculator(inputs, accumulationResults, {}, true)}
          />
        </div>
      )}
      
      {/* Post-run copy: free users see reset message, subscribers see autosave below */}
      {accumulationResults && !accumulationResults.error && (
        <p style={{ textAlign: 'center', fontSize: '13px', color: '#6b7280', marginTop: '16px' }}>
          {SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.SAVE_RESTORE_SCENARIO)
            ? 'Your inputs stay on this page. Saved automatically — subscriber benefit.'
            : 'Inputs reset after each run. Subscribers keep their inputs — subscribe to save and restore.'}
        </p>
      )}
      
      {/* Autosave Indicator (subscriber benefit) */}
      {lastSaved && (
        <div style={{ 
          textAlign: 'center', 
          padding: '12px', 
          marginTop: '20px',
          fontSize: '14px',
          color: '#059669',
          backgroundColor: '#ecfdf5',
          border: '1px solid #a7f3d0',
          borderRadius: '8px'
        }}>
          <span style={{ marginRight: '8px' }}>✓</span>
          Last saved: {Math.floor((Date.now() - lastSaved) / 1000)} seconds ago — subscriber benefit
        </div>
      )}

      {/* Inputs revert / persistence explainer (free users only, after run) */}
      {showInputsRevertModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            padding: 16
          }}
          onClick={() => setShowInputsRevertModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: 16,
              maxWidth: 448,
              width: '100%',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
              padding: 32,
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowInputsRevertModal(false)}
              style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}
              aria-label="Close"
            >
              <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <div style={{ textAlign: 'center' }}>
              <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'center' }}>
                <div style={{ background: '#dbeafe', borderRadius: '50%', padding: 16 }}>
                  <svg width="48" height="48" fill="none" stroke="#2563eb" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                </div>
              </div>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111', marginBottom: 12 }}>How your inputs work</h2>
              <p style={{ color: '#4b5563', marginBottom: 24, textAlign: 'left', fontSize: 15 }}>
                <strong>Free users:</strong> Your inputs reset to defaults after each run. Re-enter your numbers each time you want to recalculate.
                <br /><br />
                <strong>Subscribers:</strong> Your inputs stay on the page and are saved automatically so you can return anytime without re-entering.
              </p>
              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  onClick={() => setShowInputsRevertModal(false)}
                  style={{ flex: 1, padding: '12px 16px', background: '#e5e7eb', color: '#1f2937', fontWeight: 600, border: 'none', borderRadius: 8, cursor: 'pointer' }}
                >
                  Got it
                </button>
                <a
                  href="#pricing"
                  onClick={() => setShowInputsRevertModal(false)}
                  style={{ flex: 1, padding: '12px 16px', background: '#2563eb', color: 'white', fontWeight: 600, borderRadius: 8, textAlign: 'center', textDecoration: 'none' }}
                >
                  Subscribe
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Consent Banner */}
      <CookieConsent />
    </div>
  );
};

export default WorkingAccumulationWrapper;
